import { memo, useCallback, useContext, useState } from "react";
import { Row } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { ToggleGroup } from "@design-system/ToggleGroup/ToggleGroup";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useIsTableReadonly } from "@hooks/useIsReadonlyOrLocked";
import { PaginatedTransactionsActionsContext } from "../PaginatedTransactionsTable.context";
import styles from "./Exclude1099Cell.module.scss";

type Exclude1099ToggleValue = "yes" | "no";

interface Props {
    row: Row<ViewTableFeatures, TransactionListItem>;
    table: { options: { meta?: object } };
}

export const Exclude1099Cell = memo(({ row, table }: Props) => {
    const transaction = row.original;
    const isExcluded = transaction.excludeFrom1099 === true;

    const { saveTransaction } = useContext(PaginatedTransactionsActionsContext);
    const { error: errorToast } = useToaster();
    const [isLoading, setIsLoading] = useState(false);
    const isReadonly = useIsTableReadonly(table);

    const toggleValue: Exclude1099ToggleValue = isExcluded ? "yes" : "no";

    const handleExclusionChange = useCallback(
        async (next: Exclude1099ToggleValue | null) => {
            if (isReadonly || (next !== "yes" && next !== "no")) {
                return;
            }

            const excludeFrom1099 = next === "yes";
            if (excludeFrom1099 === isExcluded) {
                return;
            }

            setIsLoading(true);
            try {
                await saveTransaction(transaction, { excludeFrom1099 });
            } catch {
                errorToast(
                    "Failed to update 1099 exclusion. Please try again.",
                );
            } finally {
                setIsLoading(false);
            }
        },
        [errorToast, isExcluded, isReadonly, saveTransaction, transaction],
    );

    return (
        <ViewTableCell captureClicks>
            <div className={styles.wrapper}>
                <ToggleGroup.Root<Exclude1099ToggleValue>
                    value={toggleValue}
                    onValueChange={handleExclusionChange}
                    size="lg"
                    disabled={isReadonly || isLoading}
                    data-testid="exclude-1099-toggle"
                >
                    <ToggleGroup.Item value="yes">Yes</ToggleGroup.Item>
                    <ToggleGroup.Item value="no">No</ToggleGroup.Item>
                </ToggleGroup.Root>
            </div>
        </ViewTableCell>
    );
});
Exclude1099Cell.displayName = "Exclude1099Cell";
